import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import colors from "core/colors";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";
        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "24";
const height = "24";
const viewBox = "0 0 24 24";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({
    xs: { width: 14, height: 14 },
    sm: { width: 16, height: 16 },
    md: { width: 20, height: 20 },
    lg: { width: 24, height: 24 },
    xl: { width: 40, height: 40 }
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            fill={colors.midnight}
            fillRule="evenodd"
            d="M14.613 8.997c.747.624.499 1.618-.063 2.288-.744.887-.744.887-.048 1.469 1.812 1.498 4.572.231 6.091-1.58 2.073-2.471 1.814-6.06-.477-7.973-2.257-1.886-5.797-1.562-7.99 1.054-1.178 1.404-2.09 3.614-.822 4.672.605.506 1.147.273 1.684.042.528-.226 1.051-.451 1.625.028zm2.222-3.552c-.406.485-.967.616-1.331.312s-.334-.879.073-1.363c.406-.485.967-.616 1.331-.312s.334.879-.073 1.363zm3.075 3.992c-.407.485-.968.616-1.332.312-.364-.304-.334-.879.073-1.363.406-.485.968-.616 1.332-.312.363.304.341.869-.073 1.363zm-.274-3.077c-.406.485-.968.616-1.332.312-.364-.304-.333-.879.073-1.363.406-.485.968-.616 1.332-.312.364.304.333.879-.073 1.363z"
            clipRule="evenodd"
            key="key-0"
        />
        <path
            fill={colors.midnight}
            d="M2.146 9.304a.498.498 0 010-.705l1.573-1.57a.5.5 0 01.707 0l9.116 9.1-2.279 2.274-9.117-9.099zM14.647 21.349c.093.003.188.006.286.011.396.022 1.37.178 1.712.409.04.027.09.064.149.11.415.322 1.097-.046.868-.52a24.976 24.976 0 00-.79-1.46l-.171-.303c-.546-.97-1.56-1.93-2.475-2.47l-2.121 2.117c.14.695.645 1.214 1.146 1.68.425.395.875.409 1.396.426z"
            key="key-1"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g, &&& circle",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "Artist"
});
